<script setup lang="ts">
/**
 * La relecture des derniers messages de test d'un destinataire (écran 6,
 * `rendreTest` S:6964-7000), en lecture.
 *
 * Ce que l'utilisateur doit vivre : sous la ligne d'une personne prévenue, il
 * demande « Relire le résultat » et lit, en toutes lettres, ce que sont devenus
 * ses derniers messages de test, sans aucun chiffre du numéro. La lecture part
 * AU CLIC, une fois par clic : jamais au chargement, jamais en boucle. Avec le
 * Basique, le bouton reste inactif avec la raison de la matrice.
 */
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import { useAcces } from "@modules/alarmes-v2/acces/useAcces"
import { useResultatsTestStore } from "@modules/alarmes-v2/stores/resultatsTest.store"
import { CODE_INDISPONIBLE } from "@modules/alarmes-v2/services/appel"
import { cleDuRefus } from "@modules/alarmes-v2/utils/refus"
import { phraseDuTest } from "@modules/alarmes-v2/utils/prevenus"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()
const tr = t as unknown as Traduire

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    /** La surveillance. */
    idInstance: string
    /** Le destinataire. */
    idUser: string
}>()

const resultats = useResultatsTestStore()
const { acces } = useAcces()

// ================================
// COMPUTED
// ================================
/** Le verdict de la matrice pour « Lire le résultat d'un message de test ». */
const getAcces = computed(() => acces("test_lire_resultat"))

/** La relecture demandée, ou `null`. */
const getResultat = computed(() =>
    resultats.resultat(props.idInstance, props.idUser)
)

/** Les lignes relues, en toutes lettres. */
const getLignes = computed(() =>
    (getResultat.value?.lignes ?? []).map((l) => phraseDuTest(l, tr))
)

/** La phrase d'un échec de lecture. */
const getPhraseErreur = computed((): string => {
    const e = getResultat.value?.erreur
    if (!e || e.code === CODE_INDISPONIBLE)
        return t("alarmesV2.tests.indisponible")
    return t(cleDuRefus(e))
})

/** Les classes d'un ton. */
const TONS: Record<string, string> = {
    ok: "text-emerald-800",
    neutre: "text-surface-800",
    warn: "text-amber-800",
    danger: "text-red-800",
}

// ================================
// METHODS
// ================================
/** Une lecture, au clic. */
function onRelire(): void {
    void resultats.relire(props.idInstance, props.idUser)
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div
        v-if="getAcces.affichage !== 'absent'"
        class="flex flex-col gap-1"
        data-test="resultat-test"
    >
        <BoutonGeste
            :acces="getAcces"
            :libelle="t('alarmesV2.tests.relire')"
            classe="self-start rounded border border-surface-300 px-2 py-1 text-xs font-medium text-surface-800"
            @click="onRelire"
        />
        <p
            v-if="getResultat?.statut === 'chargement'"
            class="text-xs text-surface-600"
        >
            {{ t("alarmesV2.tests.chargement") }}
        </p>
        <p
            v-else-if="getResultat?.statut === 'erreur'"
            class="text-xs text-surface-800"
            data-test="test-erreur"
        >
            {{ getPhraseErreur }}
        </p>
        <template v-else-if="getResultat?.statut === 'pret'">
            <p
                v-if="getLignes.length === 0"
                class="text-xs text-surface-700"
                data-test="test-aucun"
            >
                {{ t("alarmesV2.tests.aucun") }}
            </p>
            <ul v-else class="flex flex-col gap-0.5">
                <li
                    v-for="(l, i) in getLignes"
                    :key="i"
                    class="text-xs"
                    :class="TONS[l.ton]"
                    data-test="test-ligne"
                >
                    {{ l.texte }}
                </li>
            </ul>
        </template>
    </div>
</template>
